import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { connectSocket, jamApi, onServerEvent } from '../lib/jamSocket.js';

const JamRoomContext = createContext(null);

export function useJamRoom() {
  const ctx = useContext(JamRoomContext);
  if (!ctx) throw new Error('useJamRoom debe usarse dentro de <JamRoomProvider>');
  return ctx;
}

const NAME_KEY = 'jam:username';
const ROOM_KEY = 'jam:roomId';
export const LOCAL_KEYS = { NAME_KEY, ROOM_KEY };

let toastSeq = 0;

/**
 * Estado global de la sesión Jam:
 *  - socketStatus / socketError: vida de la conexión;
 *  - room / users / hostId / me: identidad y plantilla;
 *  - queue / currentIndex / playback: estado canónico sincronizado del backend;
 *  - streamRefresh: última señal `stream:refresh` (el motor de audio la usa);
 *  - acciones en formato promesa con toasts en los caminos con UX.
 */
export function JamRoomProvider({ children }) {
  const [socketStatus, setSocketStatus] = useState('connecting');
  const [socketError, setSocketError] = useState(null);
  const [room, setRoom] = useState(null);
  const [me, setMe] = useState({ id: null, name: '' });
  const [hostId, setHostId] = useState(null);
  const [users, setUsers] = useState([]);
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [playback, setPlayback] = useState(null);
  const [streamRefresh, setStreamRefresh] = useState(null);
  const [toasts, setToasts] = useState([]);

  const joinedRef = useRef(null);
  const roomRef = useRef(null);
  roomRef.current = room;
  const usersRef = useRef([]);
  usersRef.current = users;
  const playbackRef = useRef(null);
  playbackRef.current = playback;

  /* ── Toasts ───────────────────────────────────────────────────────────────── */

  const pushToast = useCallback((message, kind = 'info', ttl = 4200) => {
    const id = ++toastSeq;
    setToasts((list) => [...list.slice(-4), { id, message, kind }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), ttl);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  /* ── Aplicación de snapshots del servidor ─────────────────────────────── */

  const applyPlayback = useCallback((p) => {
    // next/prev/jump y el auto-avance mueven el indice. `currentTrack` sale de
    // queue[currentIndex], asi que hay que mantenerlo al dia aqui tambien (no
    // solo via queue:state): si no, el motor de audio nunca cambia de pista.
    setCurrentIndex(p.currentIndex ?? -1);
    setPlayback({
      index: p.currentIndex ?? -1,
      playing: !!p.playing,
      positionSec: p.positionSec ?? 0,
      durationSec: p.durationSec ?? 0,
      receivedAt: Date.now(),
    });
  }, []);

  const applyRoomSnapshot = useCallback(
    (res) => {
      const r = res.room ?? {};
      setRoom({ id: r.id, name: r.name });
      setHostId(r.hostId ?? null);
      setUsers(r.users ?? []);
      setQueue(r.playlist ?? []);
      setCurrentIndex(r.currentIndex ?? -1);
      setMe((prev) => ({ ...prev, id: res.userId ?? prev.id }));
      applyPlayback({
        currentIndex: r.currentIndex ?? -1,
        playing: r.playing,
        positionSec: r.positionSec,
        durationSec: r.durationSec,
      });
    },
    [applyPlayback],
  );

  /* ── Conexión base + suscripciones broadcast ─────────────────────────── */

  useEffect(() => {
    const socket = connectSocket();

    const onConnect = () => {
      setSocketError(null);
      setSocketStatus('connected');
      setMe((prev) => ({ ...prev, id: socket.id }));
      // Reconexión: volvemos a la sala (el socket.id cambió, hay que re-join).
      if (joinedRef.current) {
        jamApi
          .joinRoom(joinedRef.current)
          .then(applyRoomSnapshot)
          .catch(() => {});
      }
    };
    const onDisconnect = () => {
      setSocketStatus('reconnecting');
    };
    const onConnectError = (err) => {
      setSocketStatus('error');
      setSocketError(err?.message ?? 'No se pudo conectar con el servidor');
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    if (socket.connected) onConnect();

    const unsubs = [
      onServerEvent('room:users', (p) => {
        setUsers(p.users ?? []);
        setHostId(p.hostId ?? null);
      }),
      onServerEvent('room:host_changed', (p) => {
        setHostId(p.hostId ?? null);
        const who = usersRef.current.find((u) => u.id === p.hostId);
        pushToast(who ? `${who.name} es el nuevo anfitrión` : 'Hay un nuevo anfitrión', 'info');
      }),
      onServerEvent('queue:state', (p) => {
        setQueue(p.playlist ?? []);
        setCurrentIndex(p.currentIndex ?? -1);
      }),
      onServerEvent('queue:added', (p) => {
        if (p.by?.id !== socket.id) {
          pushToast(`${p.by?.name ?? 'Alguien'} añadió "${String(p.track?.title ?? 'una pista').slice(0, 40)}"`, 'ok');
        }
      }),
      onServerEvent('playback:state', (p) => applyPlayback(p)),
      onServerEvent('stream:refresh', (p) => {
        setStreamRefresh({ ...p, at: Date.now() });
      }),
      onServerEvent('error:server', (p) => {
        pushToast(p.message ?? 'Error del servidor', 'error');
      }),
    ];

    return () => {
      unsubs.forEach((off) => off());
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
    };
  }, [applyPlayback, applyRoomSnapshot, pushToast]);

  /* ── Acciones ─────────────────────────────────────────────────────────── */

  function ensureRoomId() {
    const r = roomRef.current;
    if (!r) throw new JamRoomLocalError('No estás dentro de una sala');
    return r.id;
  }

  const join = useCallback(
    async ({ username, roomId }) => {
      const res = await jamApi.joinRoom({ roomId: roomId || null, username });
      joinedRef.current = { roomId: res.room.id, username };
      try {
        localStorage.setItem(NAME_KEY, username);
        localStorage.setItem(ROOM_KEY, res.room.id);
      } catch {
        /* almacenamiento no disponible: sin persistencia */
      }
      applyRoomSnapshot(res);
      setMe((prev) => ({ ...prev, name: username, id: res.userId ?? prev.id }));
      return res;
    },
    [applyRoomSnapshot],
  );

  const leave = useCallback(async () => {
    try {
      if (roomRef.current) await jamApi.leaveRoom({});
    } catch {
      /* limpiamos igualmente en el cliente */
    }
    joinedRef.current = null;
    try {
      localStorage.removeItem(ROOM_KEY);
    } catch {
      /* noop */
    }
    setRoom(null);
    setUsers([]);
    setQueue([]);
    setCurrentIndex(-1);
    setHostId(null);
    setPlayback(null);
    setStreamRefresh(null);
    try {
      history.replaceState(null, '', location.pathname);
    } catch {
      /* noop */
    }
  }, []);

  const search = useCallback((query) => {
    return jamApi.searchTrack({ query, limit: 25 });
  }, []);

  const addToQueue = useCallback(
    async (track) => {
      const res = await jamApi.addToQueue({ roomId: ensureRoomId(), track });
      const label = String(res.track?.title ?? '').slice(0, 34);
      pushToast(`Añadiste "${label}"`, 'ok');
      return res;
    },
    [pushToast],
  );

  const removeFromQueue = useCallback(async (index) => {
    return jamApi.removeFromQueue({ roomId: ensureRoomId(), index });
  }, []);

  const reorderQueue = useCallback(async (from, to) => {
    return jamApi.reorderQueue({ roomId: ensureRoomId(), from, to });
  }, []);

  const control = useCallback(
    async (action, opts = {}) => {
      try {
        return await jamApi.playbackControl({ roomId: ensureRoomId(), action, ...opts });
      } catch (err) {
        if (err.code === 'no_more_tracks') pushToast('No hay más canciones en la cola', 'info');
        else if (err.code !== 'ack_timeout') pushToast(err.message, 'error');
        throw err;
      }
    },
    [pushToast],
  );

  const syncPlayback = useCallback((state) => {
    return jamApi.syncPlayback({ roomId: ensureRoomId(), ...state });
  }, []);

  const syncRequest = useCallback(() => {
    return jamApi.syncRequest({ roomId: ensureRoomId() });
  }, []);

  const reportStreamError = useCallback(({ videoId, code }) => {
    return jamApi.reportStreamError({
      roomId: roomRef.current?.id ?? null,
      videoId,
      code,
    });
  }, []);

  /* ── Reloj del servidor (posición interpolada) ─────────────────────────── */

  const getPlaybackNow = useCallback(() => {
    const pb = playbackRef.current;
    if (!pb) return null;
    if (!pb.playing) return { ...pb, positionSec: pb.positionSec ?? 0 };
    const delta = Math.max(0, (Date.now() - pb.receivedAt) / 1000);
    return { ...pb, positionSec: (pb.positionSec ?? 0) + delta };
  }, []);

  const isHost = hostId != null && me.id != null && hostId === me.id;

  const value = useMemo(
    () => ({
      socketStatus,
      socketError,
      room,
      me,
      isHost,
      users,
      queue,
      currentIndex,
      currentTrack: currentIndex >= 0 ? (queue[currentIndex] ?? null) : null,
      playback,
      streamRefresh,
      toasts,
      getPlaybackNow,
      join,
      leave,
      search,
      addToQueue,
      removeFromQueue,
      reorderQueue,
      control,
      syncPlayback,
      syncRequest,
      reportStreamError,
      pushToast,
      dismissToast,
    }),
    [
      socketStatus, socketError, room, me, isHost, users, queue, currentIndex,
      playback, streamRefresh, toasts, getPlaybackNow, join, leave, search,
      addToQueue, removeFromQueue, reorderQueue, control, syncPlayback,
      syncRequest, reportStreamError, pushToast, dismissToast,
    ],
  );

  return <JamRoomContext.Provider value={value}>{children}</JamRoomContext.Provider>;
}

/** Error local del cliente (sin red). */
export class JamRoomLocalError extends Error {
  constructor(message) {
    super(message);
    this.name = 'JamRoomLocalError';
    this.code = 'local_error';
  }
}
